import customerKeys from '@/api/queries/customers/keyFactory';
import EntityTagControl from '@/ui/components/entityTagControl/EntityTagControl';
import FinancialSummaryCard, {
  IProps as IFinancialSummaryProps,
} from '@/ui/components/financialSummaryCard/FinancialSummaryCard';
import { Button, Card, SkeletonLoader } from '@payaca/ambrosia';
import AddCardButton from '@payaca/components/addCard/AddCardButton';
import Address2Lines from '@payaca/components/address/Address2Lines';
import GoogleMapAnchor from '@payaca/components/googleMapAnchor/GoogleMapAnchor';

import { TaggableEntityType } from '@payaca/types/tagTypes';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import useGetCustomer from '../../../../api/queries/customers/useGetCustomer';

interface IProps {
  onEditClick?: () => void;
}

const CustomerPageHeaders: FC<IProps> = (props) => {
  const { onEditClick } = props;

  const { customerId } = useParams<{ customerId: string }>();
  const queryClient = useQueryClient();
  const { customer, isLoading } = useGetCustomer(customerId);

  const history = useHistory();
  const { path, url } = useRouteMatch();

  const financialSummary = useMemo<
    IFinancialSummaryProps['financialSummary']
  >(() => {
    const totals = customer?.totals;

    if (!totals) return;

    return {
      totalProjectValue: totals.projectValue.value,
      currency: totals.acceptedValue.currency,
      toBeInvoiced: totals.acceptedValue.value - totals.sentInvoicedValue.value,
      toBePaid:
        totals.sentInvoicedValue.value - totals.completedPaymentValue.value,
      paid: totals.completedPaymentValue.value,
    };
  }, [customer]);

  if (isLoading || !customer) {
    return (
      <div>
        <SkeletonLoader.Title className="w-1/6" />

        <div className="mb-4 mt-5 grid grid-cols-3 gap-4">
          <Card>
            <Card.Body className="space-y-4">
              <SkeletonLoader.Text className="w-1/2" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="space-y-4">
              <SkeletonLoader.Text className="w-1/2" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="space-y-4">
              <SkeletonLoader.Text className="w-1/2" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
              <SkeletonLoader.Text className="w-1/4" />
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="mb-4 flex items-baseline gap-4">
          <h2>{customer.name}</h2>

          <Button variant={'link-inline'} onClick={onEditClick}>
            Edit
          </Button>
        </div>

        <EntityTagControl
          entityId={+customer.id}
          entityType={TaggableEntityType.CUSTOMER}
          entityTags={
            customer?.tags.map((tag) => ({
              id: +tag.id,
              tagText: tag.name,
              tagColour: tag.colour || '',
              isSystemManaged: false,
              archivedAt: null,
            })) || []
          }
          onEntityTagsChange={() =>
            queryClient.invalidateQueries({
              queryKey: customerKeys.customer(customerId),
            })
          }
        />
      </div>

      <div className="mb-4 grid gap-4 xl:grid-cols-3">
        {customer.primaryContact && (
          <Card>
            <Card.Body>
              <h4>Primary Contact</h4>

              <p>{customer.primaryContact.name}</p>
              <p>{customer.primaryContact.description}</p>
              <p>
                <a href={`mailto:${customer.primaryContact.email}`}>
                  {customer.primaryContact.email}
                </a>
              </p>
              <p>
                <a href={`tel:${customer.primaryContact.phone}`}>
                  {customer.primaryContact.phone}
                </a>
              </p>
            </Card.Body>
          </Card>
        )}

        {customer.billingAddress ? (
          <Card>
            <Card.Body>
              <h4>Billing Address</h4>

              <GoogleMapAnchor
                fullLocalAddress={
                  customer.billingAddress.fullLocalAddress || undefined
                }
              >
                <Address2Lines address={customer.billingAddress} />
              </GoogleMapAnchor>
            </Card.Body>
          </Card>
        ) : (
          <AddCardButton
            text="Add Billing Address"
            onClick={() => history.push(`${url}/addresses/new`)}
          />
        )}

        <FinancialSummaryCard
          title="Customer value"
          financialSummary={financialSummary}
        />
      </div>
    </>
  );
};

export default CustomerPageHeaders;
